import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  createSuperAdminPlan,
  SaPlan,
  SaPlanUpdatePayload,
  updateSuperAdminPlan,
} from "../../../helpers/super_admin_plans_helper";
import {
  centsToReaisInput,
  parseReaisToCents,
} from "../../../helpers/money";

type PlanFormState = {
  name: string;
  slug: string;
  description: string;
  visibility: "public" | "private" | "internal";
  priceReais: string;
  emailQuota: string;
  isTrialPlan: boolean;
  isActive: boolean;
};

const emptyForm = (): PlanFormState => ({
  name: "",
  slug: "",
  description: "",
  visibility: "public",
  priceReais: "0,00",
  emailQuota: "1000",
  isTrialPlan: false,
  isActive: true,
});

const planToForm = (plan: SaPlan): PlanFormState => ({
  name: plan.name,
  slug: plan.slug,
  description: plan.description || "",
  visibility:
    plan.visibility === "private" || plan.visibility === "internal"
      ? plan.visibility
      : "public",
  priceReais: centsToReaisInput(plan.base_price_cents),
  emailQuota: String(plan.email_quota),
  isTrialPlan: !!plan.is_trial_plan,
  isActive: !!plan.is_active,
});

const slugify = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

type Props = {
  isOpen: boolean;
  plan: SaPlan | null;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Create/edit de plano SA em Modal (padrão SES/suppressions).
 * Grandfathering visual: com assinantes, preço/cota/trial/slug travados (DOC 02 § 2.4).
 */
const PlanFormModal = ({ isOpen, plan, onClose, onSaved }: Props) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<PlanFormState>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [subscribers, setSubscribers] = useState(0);
  /** Família comercial ao duplicar versão (LAC-063) — null = derivar do slug no backend. */
  const [versionFamily, setVersionFamily] = useState<string | null>(null);
  /** Plano origem do “Duplicar” — para aposentar no catálogo + audit. */
  const [supersedesPlanId, setSupersedesPlanId] = useState<number | null>(null);
  const [deactivateSuperseded, setDeactivateSuperseded] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [wasActiveOnOpen, setWasActiveOnOpen] = useState(true);
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset ao fechar — evita vazar dados/erros entre create e edit.
      setForm(emptyForm());
      setEditingId(null);
      setSubscribers(0);
      setVersionFamily(null);
      setSupersedesPlanId(null);
      setDeactivateSuperseded(false);
      setSlugTouched(false);
      setSaving(false);
      setFieldError(null);
      setWasActiveOnOpen(true);
      setDeactivateConfirmed(false);
      return;
    }

    setFieldError(null);
    setSlugTouched(false);
    setSaving(false);
    setDeactivateConfirmed(false);
    setSupersedesPlanId(null);
    setDeactivateSuperseded(false);
    if (plan) {
      setEditingId(plan.id);
      setSubscribers(plan.subscriptions_count ?? 0);
      setWasActiveOnOpen(!!plan.is_active);
      setVersionFamily(plan.version_family || null);
      setForm(planToForm(plan));
    } else {
      setEditingId(null);
      setSubscribers(0);
      setWasActiveOnOpen(true);
      setVersionFamily(null);
      setForm(emptyForm());
    }
  }, [isOpen, plan]);

  // Travas comerciais só no PATCH com assinantes — create (e duplicar) liberam.
  const commercialLocked = editingId !== null && subscribers > 0;

  const title = useMemo(
    () =>
      editingId
        ? t("sa.plans.form.titleEdit")
        : t("sa.plans.form.titleNew"),
    [editingId, t]
  );

  const setField = <K extends keyof PlanFormState>(
    key: K,
    value: PlanFormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug:
        !slugTouched && !commercialLocked ? slugify(name) : prev.slug,
    }));
  };

  const duplicateAsNewVersion = () => {
    const suffix = t("sa.plans.duplicate.nameSuffix");
    const nextName = form.name.includes(suffix.trim())
      ? form.name
      : `${form.name}${suffix}`;
    const baseSlug = slugify(form.slug || form.name) || "plan";
    const nextSlug = baseSlug.endsWith("-v2") ? `${baseSlug}-new` : `${baseSlug}-v2`;
    const sourceId = editingId ?? plan?.id ?? null;
    const sourceWasActive = plan?.is_active ?? wasActiveOnOpen;

    // Mantém a family do plano origem — timeline SA agrupa as versões.
    setVersionFamily(
      (prev) => prev || plan?.version_family || form.slug || baseSlug
    );
    setSupersedesPlanId(sourceId);
    // Default: aposentar o anterior no catálogo (assinantes ficam no ID antigo).
    setDeactivateSuperseded(!!sourceWasActive);
    setEditingId(null);
    setSubscribers(0);
    setSlugTouched(true);
    setFieldError(null);
    setForm((prev) => ({
      ...prev,
      name: nextName,
      slug: nextSlug.slice(0, 64),
      isActive: true,
    }));
  };

  const formatPriceOnBlur = () => {
    const cents = parseReaisToCents(form.priceReais);
    if (cents === null) {
      return;
    }
    setField("priceReais", centsToReaisInput(cents));
  };

  const awaitingDeactivateConfirm =
    editingId !== null &&
    subscribers > 0 &&
    wasActiveOnOpen &&
    !form.isActive &&
    !deactivateConfirmed;

  const savePlan = async (opts?: { deactivateOk?: boolean }) => {
    setFieldError(null);

    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase();
    const cents = parseReaisToCents(form.priceReais);
    const quota = Number(form.emailQuota);

    if (!name) {
      setFieldError(t("sa.plans.form.validation.nameRequired"));
      return;
    }
    if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setFieldError(t("sa.plans.form.validation.slugInvalid"));
      return;
    }
    if (cents === null) {
      setFieldError(t("sa.plans.form.validation.priceInvalid"));
      return;
    }
    if (!Number.isFinite(quota) || quota < 1) {
      setFieldError(t("sa.plans.form.validation.quotaInvalid"));
      return;
    }

    const mustConfirmDeactivate =
      editingId !== null &&
      subscribers > 0 &&
      wasActiveOnOpen &&
      !form.isActive;

    // Assinantes mantêm o plano; confirmação evita desligar o catálogo sem querer.
    if (mustConfirmDeactivate && !opts?.deactivateOk && !deactivateConfirmed) {
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const payload: SaPlanUpdatePayload = {
          name,
          description: form.description.trim() || null,
          visibility: form.visibility,
          is_active: form.isActive,
        };

        // Não manda campos imutáveis se travados — evita 422 desnecessário.
        if (!commercialLocked) {
          payload.slug = slug;
          payload.base_price_cents = cents;
          payload.email_quota = quota;
          payload.is_trial_plan = form.isTrialPlan;
          payload.billing_interval = "monthly";
          payload.currency = "BRL";
        }

        const res = await updateSuperAdminPlan(editingId, payload);
        toast.success(res?.message || t("sa.plans.toast.updated"), {
          autoClose: 2500,
        });
      } else {
        const res = await createSuperAdminPlan({
          name,
          slug,
          version_family: versionFamily || undefined,
          supersedes_plan_id: supersedesPlanId || undefined,
          deactivate_superseded: supersedesPlanId
            ? deactivateSuperseded
            : undefined,
          description: form.description.trim() || null,
          visibility: form.visibility,
          currency: "BRL",
          base_price_cents: cents,
          billing_interval: "monthly",
          email_quota: quota,
          is_trial_plan: form.isTrialPlan,
          is_active: form.isActive,
        });
        toast.success(
          res?.message ||
            (supersedesPlanId && deactivateSuperseded
              ? t("sa.plans.toast.createdAndRetired")
              : t("sa.plans.toast.created")),
          {
            autoClose: 2500,
          }
        );
      }
      onSaved();
      onClose();
    } catch (err: any) {
      if (err?.code === "PLAN_PRICE_IMMUTABLE") {
        setFieldError(err?.message || t("sa.plans.error.immutable"));
      } else {
        const details =
          err?.details?.slug?.[0] ||
          err?.details?.name?.[0] ||
          err?.details?.base_price_cents?.[0] ||
          err?.details?.email_quota?.[0] ||
          err?.message;
        setFieldError(details || t("sa.plans.error.save"));
      }
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await savePlan();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => !saving && onClose()}
      centered
      size="lg"
    >
      <Form onSubmit={submit}>
        <ModalHeader toggle={() => !saving && onClose()}>{title}</ModalHeader>
        <ModalBody>
          {commercialLocked ? (
            <Alert color="warning" className="d-flex flex-column gap-2">
              <span>{t("sa.plans.grandfathering.hint")}</span>
              <div>
                <Button
                  type="button"
                  color="warning"
                  outline
                  size="sm"
                  onClick={duplicateAsNewVersion}
                  disabled={saving}
                >
                  {t("sa.plans.duplicateAsNewVersion")}
                </Button>
              </div>
            </Alert>
          ) : null}

          {fieldError ? (
            <Alert color="danger" className="py-2" toggle={() => setFieldError(null)}>
              {fieldError}
            </Alert>
          ) : null}

          {editingId === null && supersedesPlanId ? (
            <Alert color="info" className="py-2">
              <div className="form-check mb-0">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="sa-plan-deactivate-superseded"
                  checked={deactivateSuperseded}
                  onChange={(e) => setDeactivateSuperseded(e.target.checked)}
                  disabled={saving}
                />
                <Label
                  className="form-check-label"
                  for="sa-plan-deactivate-superseded"
                >
                  {t("sa.plans.duplicate.deactivateSuperseded")}
                </Label>
              </div>
              <div className="form-text mt-1 mb-0">
                {t("sa.plans.duplicate.deactivateSupersededHelp")}
              </div>
            </Alert>
          ) : null}

          {awaitingDeactivateConfirm ? (
            <Alert color="warning" className="d-flex flex-column gap-2">
              <span>
                {t("sa.plans.deactivate.confirm", { count: subscribers })}
              </span>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  color="warning"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setDeactivateConfirmed(true);
                    void savePlan({ deactivateOk: true });
                  }}
                >
                  {t("sa.plans.deactivate.confirmButton")}
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setField("isActive", true);
                    setDeactivateConfirmed(false);
                  }}
                >
                  {t("sa.plans.form.cancel")}
                </Button>
              </div>
            </Alert>
          ) : null}

          <Row className="g-3">
            <Col md={6}>
              <Label for="sa-plan-name">{t("sa.plans.form.name")}</Label>
              <Input
                id="sa-plan-name"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                disabled={saving}
                invalid={Boolean(fieldError && !form.name.trim())}
              />
            </Col>
            <Col md={6}>
              <Label for="sa-plan-slug">{t("sa.plans.form.slug")}</Label>
              <Input
                id="sa-plan-slug"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", e.target.value.toLowerCase());
                }}
                disabled={saving || commercialLocked}
              />
              <div className="form-text">{t("sa.plans.form.slugHelp")}</div>
            </Col>
            <Col xs={12}>
              <Label for="sa-plan-description">
                {t("sa.plans.form.description")}
              </Label>
              <Input
                id="sa-plan-description"
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={saving}
              />
            </Col>
            <Col md={4}>
              <Label for="sa-plan-visibility">
                {t("sa.plans.form.visibility")}
              </Label>
              <Input
                id="sa-plan-visibility"
                type="select"
                value={form.visibility}
                onChange={(e) =>
                  setField(
                    "visibility",
                    e.target.value as PlanFormState["visibility"]
                  )
                }
                disabled={saving}
              >
                <option value="public">
                  {t("sa.plans.visibility.public")}
                </option>
                <option value="private">
                  {t("sa.plans.visibility.private")}
                </option>
                <option value="internal">
                  {t("sa.plans.visibility.internal")}
                </option>
              </Input>
            </Col>
            <Col md={4}>
              <Label for="sa-plan-price">{t("sa.plans.form.price")}</Label>
              <Input
                id="sa-plan-price"
                value={form.priceReais}
                onChange={(e) => setField("priceReais", e.target.value)}
                onBlur={formatPriceOnBlur}
                disabled={saving || commercialLocked}
                inputMode="decimal"
              />
              <div className="form-text">{t("sa.plans.form.priceHelp")}</div>
              {commercialLocked ? (
                <FormFeedback className="d-block">
                  {t("sa.plans.grandfathering.lockedFields")}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md={4}>
              <Label for="sa-plan-quota">{t("sa.plans.form.quota")}</Label>
              <Input
                id="sa-plan-quota"
                type="number"
                min={1}
                value={form.emailQuota}
                onChange={(e) => setField("emailQuota", e.target.value)}
                disabled={saving || commercialLocked}
              />
            </Col>
            <Col md={6}>
              <div className="form-check form-switch mt-2">
                <Input
                  id="sa-plan-trial"
                  type="switch"
                  checked={form.isTrialPlan}
                  onChange={(e) => setField("isTrialPlan", e.target.checked)}
                  disabled={saving || commercialLocked}
                />
                <Label className="form-check-label" for="sa-plan-trial">
                  {t("sa.plans.form.trial")}
                </Label>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-check form-switch mt-2">
                <Input
                  id="sa-plan-active"
                  type="switch"
                  checked={form.isActive}
                  onChange={(e) => {
                    setDeactivateConfirmed(false);
                    setField("isActive", e.target.checked);
                  }}
                  disabled={saving}
                />
                <Label className="form-check-label" for="sa-plan-active">
                  {t("sa.plans.form.active")}
                </Label>
              </div>
              <div className="form-text">{t("sa.plans.form.activeHelp")}</div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="light"
            disabled={saving}
            onClick={onClose}
          >
            {t("sa.plans.form.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={saving || awaitingDeactivateConfirm}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("sa.plans.form.saving")}
              </>
            ) : (
              t("sa.plans.form.save")
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PlanFormModal;
